import {CabinetMesh} from 'components/customer/RoomPlanner/types';

export const PLACEHOLDER_EDIT_PARAM = 'placeholderEdit';
export const PLACEHOLDER_FACE_PARAM = 'placeholderFace';
export const PLACEHOLDER_NAME_PARAM = 'placeholderName';

type PlaceholderUserData = {
    id?: string | number;
    name?: string;
    placeholderName?: string;
    placeholderCategory?: string;
};

/**
 * Name and category live on the mesh, so edit URLs do not need to copy them.
 *
 * @param {CabinetMesh | null | undefined} group Selected placeholder mesh
 * @return {{name: string, category: string}} Display fields for the editor chrome
 */
export const getPlaceholderDisplay = (
    group?: CabinetMesh | null
): {name: string; category: string} => {
    const userData = group?.userData as PlaceholderUserData | undefined;
    return {
        name: String(userData?.placeholderName || userData?.name || ''),
        category: String(userData?.placeholderCategory || ''),
    };
};

/**
 * Query params that keep placeholder edit inside the appliance panel.
 * Name and category are read from the selected mesh, not the URL.
 *
 * @param {URLSearchParams} searchParams Current URL query
 * @param {CabinetMesh} group Selected placeholder mesh
 * @return {URLSearchParams} The params to navigate with
 */
export const buildPlaceholderEditorParams = (
    searchParams: URLSearchParams,
    group: CabinetMesh
): URLSearchParams => {
    const next = new URLSearchParams(searchParams);
    const userData = group.userData as PlaceholderUserData;

    next.set('category', 'placeholder');
    next.set(PLACEHOLDER_EDIT_PARAM, String(userData.id ?? ''));
    next.delete(PLACEHOLDER_NAME_PARAM);
    next.delete('subCategory');
    next.delete('product');
    next.delete('cabinetId');
    next.delete('favourites');
    next.delete(PLACEHOLDER_FACE_PARAM);

    /*
     * Keep the 3D selection on this placeholder. Dragging and the
     * selection highlight are gated on the `selected`/`index` params
     * (checkCabinetIfSelected), so dropping them here forced a second
     * click before the placeholder could be dragged.
     */
    const placeholderId = String(userData.id ?? '');
    next.set('selected', placeholderId);
    let index: string | undefined;
    if (isNaN(Number(placeholderId))) {
        [, index] = placeholderId.split('-');
    }
    if (index !== undefined) {
        next.set('index', index);
    } else {
        next.delete('index');
    }

    next.set('preventReset', 'true');
    return next;
};

/**
 * Query params for a cabinet editor opened in 3DRP (cart Edit or 3D Edit).
 * `preventReset` marks an edit session so clicking another cabinet in 3D
 * can swap `cabinetId`/`product` instead of dropping the selection.
 * @param {string} cabinetId Job cabinet id
 * @param {string} productType Product type id
 * @return {URLSearchParams} Params to navigate with
 */
export const buildCabinetEditorParams = (
    cabinetId: string,
    productType: string
): URLSearchParams => {
    const next = new URLSearchParams();
    next.set('product', productType);
    next.set('cabinetId', cabinetId);
    next.set('selected', cabinetId);
    next.set('index', '0');
    next.set('preventReset', 'true');
    return next;
};
